/* eslint-disable react/prop-types */
import {
  Box,
  Stack,
  Typography,
  useTheme,
  styled,
  Switch,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Link,
  IconButton,
  Paper,
} from '@mui/material';
import AddedProcesses from './AddedProcesses';
import {useDispatch, useSelector} from 'react-redux';
import ReleaseSelector from 'components/ReleaseSelector/ReleaseSelector';
import React from 'react';
import themes from 'themes';
import {IChange, ChangePageType, Roles} from 'app/types/Shared';
import {ArrowCircleRight, Refresh, UnfoldMore} from '@mui/icons-material';
import {WarningAltFilled, ChevronDown, Launch} from '@carbon/icons-react';
import ReactMarkdown from 'react-markdown';
import {selectAllChanges, selectNewProccess} from 'app/store/change/changeSlice';
import {
  resetHiddenChanges,
  selectHiddenChanges,
  selectRoleFilters,
  selectSelectedProcess,
  selectSelectedReleases,
  setHiddenChanges,
  setSelectedL1Label,
  setSelectedL2Label,
  setSelectedSubProcess,
} from 'app/store/appState/appStateSlice';
import {selectAllMaps, selectAllProcesses} from 'app/store/operatingModel/operatingModelSlice';

const StyledSwtich = styled(Switch)({
  width: 36,
  height: 18,
  padding: 0,
  margin: 2,
  '& .MuiSwitch-switchBase': {
    padding: 1,
    margin: 1,
    transitionDuration: '200ms',
    '&.Mui-checked': {
      transform: 'translateX(18px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: themes().palette.primary.main,
        opacity: 1,
        border: 0,
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        opacity: 0.5,
      },
    },
    '&.Mui-focusVisible .MuiSwitch-thumb': {
      color: '#33cf4d',
      border: '6px solid #fff',
    },
    '&.Mui-disabled .MuiSwitch-thumb': {
      color: themes().palette.mode === 'light' ? themes().palette.grey[100] : themes().palette.grey[600],
    },
    '&.Mui-disabled + .MuiSwitch-track': {
      opacity: themes().palette.mode === 'light' ? 0.7 : 0.3,
    },
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 14,
    height: 14,
  },
  '& .MuiSwitch-track': {
    borderRadius: 26 / 2,
    border: 'solid 1px lightgrey',
    backgroundColor: themes().palette.grey[500],
    opacity: 1,
    transition: themes().transitions.create(['background-color'], {
      duration: 300,
    }),
  },
});

enum ChangeSort {
  Release = 'Release',
  Type = 'Type',
  Process = 'Process',
}

type updateProps = {
  title?: string;
  description?: string;
  subtitle?: string;
  type: ChangePageType;
  px?: number;
  processNavigation?: (arg0: string) => void;
};

const ProcessUpdates = ({title, description, subtitle, type, px = 20, processNavigation}: updateProps) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [displayRole, setDisplayRoles] = React.useState(false);
  const hiddenChanges = useSelector(selectHiddenChanges);
  const [addedProcessesOpen, setAddedProcessesOpen] = React.useState(type === ChangePageType.HomePage ? true : false);
  const changeData = useSelector(selectAllChanges);
  const [currentSort, setCurrentSort] = React.useState<ChangeSort | null>(null);
  const [sortedChanges, setSortedChanges] = React.useState<Array<IChange>>(changeData);
  const changeRef = React.useRef(changeData.length);
  const selectedProcess = useSelector(selectSelectedProcess);
  const selectedReleases = useSelector(selectSelectedReleases);
  const allMaps = useSelector(selectAllMaps);
  const allProcesses = useSelector(selectAllProcesses);
  const selectedRoles = useSelector(selectRoleFilters);
  const newProcesses = useSelector(selectNewProccess);

  React.useEffect(() => {
    if (changeRef.current !== changeData.length) {
      setSortedChanges(changeData);
      changeRef.current = changeData.length;
    }
  }, [changeData]);

  const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDisplayRoles(event.target.checked);
  };

  const handleChangeSort = (sort: ChangeSort) => {
    switch (sort) {
      case ChangeSort.Release: {
        if (currentSort === ChangeSort.Release) {
          setCurrentSort(null);
          setSortedChanges(changeData);
          return;
        }
        const sortedArr = [...sortedChanges].sort((a: IChange, b: IChange) => {
          const getVersionPart = (version: string, index: number) => {
            return parseInt(version.split('.')[index], 10) || 0;
          };

          const aYear = getVersionPart(a.release, 0);
          const bYear = getVersionPart(b.release, 0);

          const aMonth = getVersionPart(a.release, 1);
          const bMonth = getVersionPart(b.release, 1);

          const aRevision = getVersionPart(a.release, 2);
          const bRevision = getVersionPart(b.release, 2);

          if (aYear !== bYear) return bYear - aYear;
          if (aMonth !== bMonth) return bMonth - aMonth;
          return bRevision - aRevision;
        });
        setSortedChanges(sortedArr);
        return;
      }
      case ChangeSort.Type: {
        if (currentSort === ChangeSort.Type) {
          setCurrentSort(null);
          setSortedChanges(changeData);
          return;
        }
        const sortedArr = [...sortedChanges].sort((a: IChange, b: IChange) => {
          if (a.delta_type < b.delta_type) {
            return -1;
          } else if (a.delta_type > b.delta_type) {
            return 1;
          }
          return 0;
        });
        setSortedChanges(sortedArr);
        return;
      }
      case ChangeSort.Process: {
        if (currentSort === ChangeSort.Process) {
          setCurrentSort(null);
          setSortedChanges(changeData);
          return;
        }
        const sortedArr = [...sortedChanges].sort((a: IChange, b: IChange) => {
          if (a.delta_ref < b.delta_ref) {
            return -1;
          } else if (a.delta_ref > b.delta_ref) {
            return 1;
          }
          return 0;
        });
        setSortedChanges(sortedArr);
        return;
      }
      default:
        return;
    }
  };

  type releaseProps = {
    change: IChange;
    processId?: string;
    stepId?: string;
  };

  const findProcess = (change: IChange) => {
    if (change.object === 'process') {
      const process = change.process_change?.[0]?.process?.name;
      if (process) {
        return process;
      }
    } else if (change.object === 'step') {
      const processKey = change.step_change?.[0]?.step?.process_key;
      if (processKey) {
        const process = allProcesses?.find((p) => p.process_id === processKey)?.name;
        if (process) {
          return process;
        }
      }
    }

    return '';
  };

  const ReleaseBar = ({change, processId, stepId}: releaseProps) => {
    return (
      <Box width='100%' minHeight='40px' border={`1px solid ${theme.palette.grey[500]}`}>
        <Accordion color='white'>
          <AccordionSummary expandIcon={<ChevronDown fontSize='large' style={{color: theme.palette.grey[800]}} />}>
            <Box width='100%' height='100%'>
              <Grid container direction='row' spacing={1}>
                <Grid item xs={type === ChangePageType.HomePage ? 1.25 : 1.5}>
                  <Typography>{change.release}</Typography>
                </Grid>
                <Grid item xs={type === ChangePageType.HomePage ? 1.25 : 1.5}>
                  <Typography>{change.delta_ref}</Typography>
                </Grid>
                {type === ChangePageType.HomePage && (
                  <Grid item xs={2.5}>
                    <Typography>{findProcess(change)}</Typography>
                    {processId && <Typography>{processId}</Typography>}
                    {stepId && <Typography>{stepId}</Typography>}
                  </Grid>
                )}
                <Grid item xs={type === ChangePageType.HomePage ? 7 : 9}>
                  <Typography variant='h5'>{`${change.delta_type == null ? '' : change.delta_type} ${
                    change.object == null ? '' : change.object
                  } ${change.object_key == null ? '' : change.object_key}`}</Typography>
                </Grid>
              </Grid>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Stack direction='column' spacing={2}>
              <Typography variant='h5'>Type: {change.delta_type}</Typography>
              <ReactMarkdown>{change.label}</ReactMarkdown>
            </Stack>
          </AccordionDetails>
        </Accordion>
      </Box>
    );
  };

  const getFilteredChanges = () => {
    const hasRole = (roles: Array<Roles>) => {
      let flag = false;
      roles.map((r) => {
        if (selectedRoles.some((role) => role === r.role.role_category.label)) {
          flag = true;
        }
      });
      return flag;
    };

    if (selectedReleases?.length === 0) {
      return [];
    }
    const filteredChanges = sortedChanges.filter((change) => !hiddenChanges?.includes(change.id));
    if (type === ChangePageType.HomePage) {
      if (displayRole) {
        return filteredChanges.filter((change) => {
          if (change.object_link === 'step') {
            const stepChange = change.step_change?.[0]?.step;
            if (stepChange) {
              return hasRole(stepChange.raci);
            }
          } else if (change.object_link === 'process') {
            const processChange = change.process_change?.[0]?.process;
            if (processChange) {
              return hasRole(processChange.roles);
            }
          }
          return false;
        });
      }
      return filteredChanges;
    } else {
      return filteredChanges.filter((change) => {
        if (change.object_link === 'step') {
          const stepChange = change.step_change?.[0]?.step;
          if (stepChange) {
            if (stepChange.process_key === selectedProcess?.process_id) {
              if (displayRole) {
                return hasRole(stepChange.raci);
              } else return true;
            } else return false;
          }
        } else if (change.object_link === 'process') {
          if (change.object_key === selectedProcess?.process_id) {
            const processChange = change.process_change?.[0]?.process;
            if (displayRole && processChange) {
              return hasRole(processChange.roles);
            } else return true;
          } else return false;
        }
        return false;
      });
    }
  };

  const handleArrowClick = (change: IChange) => {
    if (change.object_link === 'process') {
      processNavigation && processNavigation(change.object_key);
    } else if (change.object_link === 'step') {
      const process_map_node = change.step_change?.[0]?.step.j_process_map_node_step?.[0]?.process_map_node;
      if (process_map_node) {
        const diagram_link = process_map_node.diagram_key;
        if (diagram_link) {
          const map = allMaps?.find((map) => map.key === diagram_link);
          if (map) {
            processNavigation && type === ChangePageType.HomePage && processNavigation(map.process_name);
            dispatch(setSelectedL1Label(map.l1_label));
            dispatch(setSelectedSubProcess(map.l1_label_order));
            if (map.l2_label) dispatch(setSelectedL2Label(map.l2_label));
          }
        }
      }
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        px: `${px}px`,
        py: 2,
        background: theme.palette.grey[100],
      }}
    >
      <Stack direction='row' alignItems='flex-end' spacing={1}>
        {type === ChangePageType.HomePage && <WarningAltFilled fontSize='medium' style={{color: 'black'}} />}
        <Typography variant='h4' fontSize={16}>
          {title}
        </Typography>
        <Box flexGrow={1} display='flex' justifyContent='flex-end'>
          <Link color={theme.palette.primary.main} underline='hover' sx={{textAlign: 'center', display: 'none'}}>
            <Stack direction='row' spacing={1} alignItems='center'>
              <Typography>Submit a Change Request</Typography>
              <Launch height='18px' width='18px' color={theme.palette.primary.main} />
            </Stack>
          </Link>
        </Box>
      </Stack>
      {description && (
        <Grid container direction='row'>
          <Grid item xs={10} md={6} lg={4} xl={3}>
            <Typography variant='h6'>{description}</Typography>
          </Grid>
        </Grid>
      )}
      <Box width='100%' py={2}>
        {addedProcessesOpen && (
          <AddedProcesses
            storybook={true}
            processChanges={newProcesses.map((p) => p.object_key)}
            closeCallback={() => setAddedProcessesOpen(false)}
          />
        )}
      </Box>
      <Stack direction='row'>
        <Box display='flex' flexGrow={1}>
          <Stack direction='row' spacing={1} alignItems='center'>
            {type === ChangePageType.ProcessPage && (
              <WarningAltFilled fontSize='small' style={{color: theme.palette.grey[800]}} />
            )}
            <Typography variant='h4'>{subtitle}</Typography>
          </Stack>
        </Box>
        <Stack direction='row' spacing={1} alignItems='center' justifyContent='center'>
          <Typography variant='h6'>Only display updates relevant to my role</Typography>
          <StyledSwtich onChange={handleSwitchChange} checked={displayRole} />
        </Stack>
      </Stack>
      <ReleaseSelector />
      <Grid container direction='row' spacing={1} p='8px'>
        <Grid item xs={10} lg={11} container direction='row' spacing={1} px='16px'>
          <Grid item xs={type === ChangePageType.HomePage ? 1.25 : 1.5}>
            <Box
              display='flex'
              justifyContent='flex-start'
              alignItems='center'
              sx={{cursor: 'pointer'}}
              onClick={() => handleChangeSort(ChangeSort.Release)}
            >
              <Typography variant='h5'>{ChangeSort.Release}</Typography>
              <UnfoldMore fontSize='medium' sx={{color: theme.palette.grey[800]}} />
            </Box>
          </Grid>
          <Grid item xs={type === ChangePageType.HomePage ? 1.25 : 1.5}>
            <Box
              display='flex'
              justifyContent='flex-start'
              alignItems='center'
              sx={{cursor: 'pointer'}}
              onClick={() => handleChangeSort(ChangeSort.Type)}
            >
              <Typography variant='h5'>{ChangeSort.Type}</Typography>
              <UnfoldMore fontSize='medium' sx={{color: theme.palette.grey[800]}} />
            </Box>
          </Grid>
          {type === ChangePageType.HomePage && (
            <Grid item xs={2.5}>
              <Box
                display='flex'
                justifyContent='flex-start'
                alignItems='center'
                sx={{cursor: 'pointer'}}
                onClick={() => handleChangeSort(ChangeSort.Process)}
              >
                <Typography variant='h5'>{ChangeSort.Process}</Typography>
                <UnfoldMore fontSize='medium' sx={{color: theme.palette.grey[800]}} />
              </Box>
            </Grid>
          )}
          <Grid item xs={type === ChangePageType.HomePage ? 7 : 9}>
            <Typography variant='h5'>Description</Typography>
          </Grid>
        </Grid>
        <Grid item xs={1} lg={0.5} />
        <Grid item xs={1} lg={0.5} display='flex' justifyContent='center' alignItems='center'>
          <IconButton sx={{p: 0}} disableRipple disableFocusRipple onClick={() => dispatch(resetHiddenChanges())}>
            <Refresh fontSize='medium' sx={{color: theme.palette.grey[800], transform: 'scaleX(-1)'}} />
          </IconButton>
        </Grid>
      </Grid>
      <Paper
        sx={{
          bgcolor: theme.palette.grey[100],
          maxHeight: '800px',
          overflowY: 'auto',
          minWidth: 0,
        }}
      >
        <Stack direction='column' spacing={0.5}>
          {getFilteredChanges().length ? (
            getFilteredChanges().map((change, index) => (
              <Grid container direction='row' key={`CHANGE-${index}`}>
                <Grid item xs={10} lg={11}>
                  <ReleaseBar change={change} />
                </Grid>
                <Grid item xs={1} lg={0.5} display='flex' justifyContent='center' alignItems='center'>
                  {(change.object_link === 'process' || change.object_link === 'step') && (
                    <IconButton sx={{p: 0}} disableRipple onClick={() => handleArrowClick(change)}>
                      <ArrowCircleRight sx={{color: theme.palette.primary.main, width: 28, height: 28}} />
                    </IconButton>
                  )}
                </Grid>
                <Grid item xs={1} lg={0.5} display='flex' justifyContent='center' alignItems='center'>
                  <Link
                    variant='h3'
                    fontWeight={400}
                    color={theme.palette.primary.main}
                    underline='hover'
                    onClick={() => dispatch(setHiddenChanges([...hiddenChanges, change.id]))}
                    sx={{
                      cursor: 'pointer',
                    }}
                  >
                    Hide
                  </Link>
                </Grid>
              </Grid>
            ))
          ) : (
            <Box width='100%' height='100%' display='flex' justifyContent='center' alignItems='center' py={6}>
              {type === ChangePageType.HomePage && (
                <Typography variant='h3'>
                  {selectedReleases?.length
                    ? `No Changes for the selected releases ${selectedReleases?.join(', ')}`
                    : `Select release(s) above to show updates`}
                </Typography>
              )}
              {type === ChangePageType.ProcessPage && (
                <Typography variant='h3'>
                  {selectedReleases?.length
                    ? `No Changes to ${selectedProcess?.name} for the selected releases ${selectedReleases?.join(', ')}`
                    : `Select release(s) above to show updates to ${selectedProcess?.name}`}
                </Typography>
              )}
            </Box>
          )}
        </Stack>
      </Paper>
    </Box>
  );
};
export default ProcessUpdates;
